import React from 'react';
import { useQuery } from '@apollo/react-hooks';

import '../../scss/_root-component.scss';

import { productDetail, productDetailVariables } from 'src/queries/__generated__/productDetail';
import productQuery from 'src/queries/productDetail.graphql';
import { getUrlKeyForProduct } from 'src/util/getUrlKeyForProduct';
import { useDeps } from 'src/hooks/useDeps';
import { LoaderMinHeight } from 'src/components/Helpers/Skeleton';
import { ErrorComponent } from 'src/components/Error/ErrorComponent';
import { NotFound } from 'src/components/NotFound/NotFound';
import {
    cleanProduct,
    DataSheet,
    productPrice,
    Specification,
} from 'src/components/ProductDetail/utils/product-detail.types';
import { productAttrsFromQuery } from 'src/components/ProductDetail/utils/product-attrs.types';
import { ProductDetailWrapper } from 'src/pages/Product/ProductDetailWrapper';
import { Reviews } from 'src/components/Reviews/reviews.types';

type ProductRootInnerProps = {
    id: number;
    pathname: string;
    isComingSoon?: boolean;
};

export const ProductRootInner: React.FC<ProductRootInnerProps> = React.memo((props) => {
    /**
     * Make the initial Product Query
     */
    const { loading, error, data } = useQuery<productDetail, productDetailVariables>(productQuery, {
        variables: { urlKey: getUrlKeyForProduct(props.pathname), includeReviews: true, id: props.id },
        ssr: useDeps().env.SSR_GQL,
    });

    /**
     * Loading handler
     */
    if (loading) {
        return <LoaderMinHeight />;
    }

    /**
     * Error handling
     */
    if (error) {
        return <ErrorComponent message={error?.message} />;
    }

    if (!data) {
        return <NotFound />;
    }

    if (!Array.isArray(data.productDetail?.items)) {
        return <NotFound />;
    }

    const first = data.productDetail?.items[0];

    if (!first) {
        return <NotFound />;
    }

    const isComingSoon = Boolean(props.isComingSoon);

    /**
     * Clean up of product data
     */
    const product = cleanProduct(first, isComingSoon);
    const attrs = productAttrsFromQuery(first, isComingSoon);

    const price = productPrice(
        first.price_range.minimum_price?.regular_price?.value ?? 0,
        first.price_range.maximum_price?.regular_price?.value ?? 0,
        first.price_range.minimum_price?.final_price?.value,
        first.price_range.maximum_price?.final_price?.value,
        first.special_price,
        first.woodies,
    );

    const dataSheets = first.product_datasheets as DataSheet[];
    const specifications = (first.woodies?.specifications || []) as Specification[];
    const benefits = (first.benefits || '').split(/\r?\n/g).filter(Boolean);

    return (
        <ProductDetailWrapper
            isComingSoon={props.isComingSoon}
            product={product}
            price={price}
            reviews={data.yotpoReviewSummary as Reviews}
            specifications={specifications}
            dataSheets={dataSheets}
            benefits={benefits}
            attrs={attrs}
            pathname={props.pathname}
        />
    );
});

export default ProductRootInner;
